import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { PermissionService } from '@app/core/services/permission.service';
import {
  DeliveryRetryConfigItem,
  DeliveryRetryFeatureService,
} from './services/delivery-retry-config.service';

@Component({
  selector: 'app-delivery-retry-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    InputNumberModule,
    ToastModule,
    ToggleSwitchModule,
  ],
  providers: [MessageService],
  templateUrl: './delivery-retry-config.component.html',
  styleUrls: ['./delivery-retry-config.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryRetryConfigComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly feature = inject(DeliveryRetryFeatureService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.delivery_retry.edit',
    );
  });

  loading = signal(false);
  saving = signal(false);
  config = signal<DeliveryRetryConfigItem>({
    retryDelayMinutes: 120,
    enabled: true,
  });

  /** Last successfully persisted minutes — used to skip no-op blur saves. */
  private lastSavedMinutes: number | null = null;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.feature.getConfig().subscribe({
      next: (item) => {
        this.config.set(item);
        this.lastSavedMinutes = item.retryDelayMinutes;
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant('delivery_retry_config.load_failed'),
        });
      },
    });
  }

  onMinutesChange(value: number | null): void {
    this.config.update((c) => ({
      ...c,
      retryDelayMinutes: value ?? 120,
    }));
  }

  onMinutesBlur(): void {
    if (!this.canUpdate() || this.saving() || this.loading()) {
      return;
    }
    const minutes = this.config().retryDelayMinutes;
    if (!minutes || minutes < 1) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('delivery_retry_config.invalid_minutes'),
      });
      return;
    }
    if (minutes === this.lastSavedMinutes) {
      return;
    }
    this.persistConfig();
  }

  onEnabledChange(value: boolean): void {
    if (!this.canUpdate() || this.saving() || this.loading()) {
      return;
    }
    const previous = this.config().enabled;
    if (previous === value) {
      return;
    }
    this.config.update((c) => ({ ...c, enabled: value }));
    this.persistConfig({
      onError: () => this.config.update((c) => ({ ...c, enabled: previous })),
    });
  }

  private persistConfig(options?: { onError?: () => void }): void {
    if (!this.canUpdate()) {
      return;
    }
    const current = this.config();
    if (!current.retryDelayMinutes || current.retryDelayMinutes < 1) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning'),
        detail: this.translate.instant('delivery_retry_config.invalid_minutes'),
      });
      options?.onError?.();
      return;
    }

    this.saving.set(true);
    this.feature.updateConfig(current).subscribe({
      next: (saved) => {
        this.config.set(saved);
        this.lastSavedMinutes = saved.retryDelayMinutes;
        this.saving.set(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success'),
          detail: this.translate.instant('delivery_retry_config.save_success'),
        });
      },
      error: () => {
        this.saving.set(false);
        options?.onError?.();
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.messages.error'),
          detail: this.translate.instant('delivery_retry_config.save_failed'),
        });
      },
    });
  }
}
